"use client";

import { useMemo } from "react";
import {
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type {
  AVERAGE_RATE_OF_CHANGE_CALCULATOR_STRINGS,
  RateView,
} from "@/components/tools/average-rate-of-change-calculator";
import {
  decimalText,
  pointValueText,
  shown,
} from "@/components/tools/calculus/format";
import { FunctionGraph } from "@/components/tools/calculus/function-graph";
import { MathTable } from "@/components/tools/calculus/math-table";
import { MathView } from "@/components/tools/calculus/math-view";
import type { PlaneScales } from "@/components/tools/math/coordinate-plane";
import type { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { div, num, sub } from "@/lib/tool-kit/calculus/ast";
import { autoWindow, type Window } from "@/lib/tool-kit/calculus/plot";
import { toLatex } from "@/lib/tool-kit/calculus/print";
import {
  approachTable,
  averageRate,
  consecutiveRates,
  meanValuePoints,
} from "@/lib/tool-kit/calculus/rate";
import { simplify } from "@/lib/tool-kit/calculus/simplify";
import { lineThrough } from "@/lib/tool-kit/calculus/tangent";
import { valueAt } from "@/lib/tool-kit/calculus/value";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

type Strings = typeof AVERAGE_RATE_OF_CHANGE_CALCULATOR_STRINGS;
type Fmt = ReturnType<typeof useToolNumber>;
interface Units {
  x: string;
  y: string;
}
type FunctionView = Extract<RateView, { kind: "function" }>;
type TableView = Extract<RateView, { kind: "table" }>;

const H_STEPS = [1, 0.1, 0.01, 0.001, 0.0001];

function digits(fmt: Fmt, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return value === 0 ? fmt(0) : fmt(value, { maximumSignificantDigits: 8 });
}

function Interpretation({
  a,
  b,
  dy,
  rate,
  units,
  strings,
  fmt,
}: {
  a: number;
  b: number;
  dy: number;
  rate: number;
  units: Units;
  strings: Strings;
  fmt: Fmt;
}) {
  let direction: string = strings.unchanged;
  if (dy > 0) {
    direction = strings.increases;
  } else if (dy < 0) {
    direction = strings.decreases;
  }
  const values = {
    a: digits(fmt, a),
    b: digits(fmt, b),
    direction,
    delta: digits(fmt, Math.abs(dy)),
    span: digits(fmt, Math.abs(b - a)),
    rate: digits(fmt, rate),
    xUnit: units.x,
    yUnit: units.y,
  };
  const useUnits = units.x !== "" && units.y !== "";
  return (
    <div className="space-y-1">
      <h3 className={WIDGET_H3}>{strings.interpretationHeading}</h3>
      <p className="text-foreground text-sm">
        {t(
          useUnits ? strings.interpretationUnits : strings.interpretationPlain,
          values
        )}
      </p>
    </div>
  );
}

function Stats({
  rate,
  dy,
  dx,
  ya,
  yb,
  exact,
  strings,
  fmt,
  units,
}: {
  rate: number;
  dy: number;
  dx: number;
  ya: number;
  yb: number;
  exact: string | null;
  strings: Strings;
  fmt: Fmt;
  units: Units;
}) {
  const suffix =
    units.x !== "" && units.y !== "" ? ` ${units.y}/${units.x}` : "";
  return (
    <StatGrid>
      <StatCard
        emphasis
        label={strings.rateLabel}
        value={`${digits(fmt, rate)}${suffix}`}
      />
      <StatCard label={strings.deltaYLabel} value={digits(fmt, dy)} />
      <StatCard label={strings.deltaXLabel} value={digits(fmt, dx)} />
      <StatCard label={strings.startValueLabel} value={digits(fmt, ya)} />
      <StatCard label={strings.endValueLabel} value={digits(fmt, yb)} />
      {exact ? <StatCard label={strings.exactLabel} value={exact} /> : null}
    </StatGrid>
  );
}

function FunctionRate({
  view,
  units,
  strings,
  fmt,
}: {
  view: FunctionView;
  units: Units;
  strings: Strings;
  fmt: Fmt;
}) {
  const { a, b, f, df, decimals } = view;
  const ya = f(a.value);
  const yb = f(b.value);
  const bad = [a.value, b.value].find((x) => !Number.isFinite(f(x)));
  const rate = averageRate(ya, yb, a.value, b.value);
  const atA = valueAt(view.ast, "x", a.ast, a.value);
  const atB = valueAt(view.ast, "x", b.ast, b.value);
  const exactNode =
    atA.exact && atB.exact
      ? simplify(div(sub(atB.exact, atA.exact), sub(b.ast, a.ast)))
      : null;
  const exactText =
    exactNode && exactNode.kind !== "num" ? shown(exactNode, decimals) : null;
  const slope = exactNode ?? num(rate);
  const secant = lineThrough(
    slope,
    rate,
    a.ast,
    atA.exact ?? num(ya),
    ya,
    a.value
  );
  const points = useMemo(
    () => meanValuePoints(df, rate, a.value, b.value),
    [df, rate, a.value, b.value]
  );
  const approach = approachTable(f, a.value, H_STEPS);
  const instant = valueAt(view.derivative, "x", a.ast, a.value);
  const lo = Math.min(a.value, b.value);
  const hi = Math.max(a.value, b.value);
  const window = useMemo<Window>(() => {
    const pad = (hi - lo) * 0.3;
    const base = autoWindow([f], lo - pad, hi + pad);
    return {
      ...base,
      yMin: Math.min(base.yMin, ya, yb),
      yMax: Math.max(base.yMax, ya, yb),
    };
  }, [f, lo, hi, ya, yb]);
  if (bad !== undefined) {
    return (
      <ErrorNotice>
        {t(strings.undefinedAt, { at: digits(fmt, bad) })}
      </ErrorNotice>
    );
  }
  const c = points[0];
  return (
    <>
      <Stats
        dx={b.value - a.value}
        dy={yb - ya}
        exact={exactText}
        fmt={fmt}
        rate={rate}
        strings={strings}
        units={units}
        ya={ya}
        yb={yb}
      />
      <Interpretation
        a={a.value}
        b={b.value}
        dy={yb - ya}
        fmt={fmt}
        rate={rate}
        strings={strings}
        units={units}
      />
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.secantHeading}</h3>
        {secant.slopeIntercept ? (
          <MathView
            label={`y = ${shown(secant.slopeIntercept, decimals)}`}
            latex={`y = ${toLatex(secant.slopeIntercept, { decimals })}`}
          />
        ) : null}
        <p className="text-muted-foreground text-sm">{strings.secantNote}</p>
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.approachHeading}</h3>
        <MathTable
          head={[strings.columnH, strings.columnInterval, strings.columnRate]}
          rows={approach.map((row) => [
            digits(fmt, row.h),
            `[${digits(fmt, a.value)}, ${digits(fmt, a.value + row.h)}]`,
            digits(fmt, row.rate),
          ])}
        />
        <p className="text-muted-foreground text-sm">
          {t(strings.approachNote, {
            a: shown(a.ast, decimals),
            derivative: pointValueText(instant, fmt, "—", decimals),
          })}
        </p>
      </div>
      <div className="space-y-1">
        <h3 className={WIDGET_H3}>{strings.mvtHeading}</h3>
        {points.length > 0 ? (
          <p className="text-foreground text-sm">
            {t(strings.mvtText, {
              points: points
                .slice(0, 3)
                .map((p) => decimalText(fmt, p))
                .join(", "),
            })}
          </p>
        ) : (
          <Notice>{strings.mvtNone}</Notice>
        )}
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
        <FunctionGraph
          downloadPngLabel={strings.downloadPng}
          downloadSvgLabel={strings.downloadSvg}
          filename="average-rate-of-change-graph"
          points={[
            {
              x: a.value,
              y: ya,
              color: CHART_COLORS[3],
              label: t(strings.pointLabelText, {
                x: digits(fmt, a.value),
                y: digits(fmt, ya),
              }),
              below: true,
            },
            {
              x: b.value,
              y: yb,
              color: CHART_COLORS[3],
              label: t(strings.pointLabelText, {
                x: digits(fmt, b.value),
                y: digits(fmt, yb),
              }),
            },
          ]}
          series={[
            { f, color: CHART_COLORS[0], label: strings.seriesFunction },
            {
              f: (x) => ya + rate * (x - a.value),
              color: CHART_COLORS[3],
              dashed: true,
              label: t(strings.seriesSecant, { rate: digits(fmt, rate) }),
            },
            ...(c === undefined
              ? []
              : [
                  {
                    f: (x: number) => f(c) + rate * (x - c),
                    color: CHART_COLORS[2],
                    dashed: true,
                    label: strings.seriesTangent,
                  },
                ]),
          ]}
          title={t(strings.graphTitleFunction, {
            a: digits(fmt, a.value),
            b: digits(fmt, b.value),
          })}
          window={window}
        />
      </div>
    </>
  );
}

function DataLine({
  points,
  scales,
}: {
  points: { x: number; y: number }[];
  scales: PlaneScales;
}) {
  const path = points
    .map(
      (p, i) =>
        `${i === 0 ? "M" : "L"}${scales.x(p.x).toFixed(1)} ${scales.y(p.y).toFixed(1)}`
    )
    .join(" ");
  return (
    <>
      <path d={path} fill="none" stroke={CHART_COLORS[0]} strokeWidth={2} />
      {points.map((p) => (
        <circle
          cx={scales.x(p.x)}
          cy={scales.y(p.y)}
          fill={CHART_COLORS[0]}
          key={`${p.x}-${p.y}`}
          r={3.5}
        />
      ))}
    </>
  );
}

function TableRate({
  view,
  units,
  strings,
  fmt,
}: {
  view: TableView;
  units: Units;
  strings: Strings;
  fmt: Fmt;
}) {
  const { points, a, b, ya, yb } = view;
  const rate = averageRate(ya, yb, a, b);
  const rows = consecutiveRates(points);
  const window = useMemo<Window>(() => {
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const xPad = (Math.max(...xs) - Math.min(...xs)) * 0.08 || 1;
    const yPad = (Math.max(...ys) - Math.min(...ys)) * 0.1 || 1;
    return {
      xMin: Math.min(...xs) - xPad,
      xMax: Math.max(...xs) + xPad,
      yMin: Math.min(...ys) - yPad,
      yMax: Math.max(...ys) + yPad,
    };
  }, [points]);
  return (
    <>
      <Stats
        dx={b - a}
        dy={yb - ya}
        exact={null}
        fmt={fmt}
        rate={rate}
        strings={strings}
        units={units}
        ya={ya}
        yb={yb}
      />
      <Interpretation
        a={a}
        b={b}
        dy={yb - ya}
        fmt={fmt}
        rate={rate}
        strings={strings}
        units={units}
      />
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.tableRatesHeading}</h3>
        <MathTable
          head={[
            strings.columnFrom,
            strings.columnTo,
            strings.columnDy,
            strings.columnDx,
            strings.columnRate,
          ]}
          rows={rows.map((r) => [
            digits(fmt, r.from.x),
            digits(fmt, r.to.x),
            digits(fmt, r.to.y - r.from.y),
            digits(fmt, r.to.x - r.from.x),
            digits(fmt, r.rate),
          ])}
        />
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
        <FunctionGraph
          downloadPngLabel={strings.downloadPng}
          downloadSvgLabel={strings.downloadSvg}
          filename="average-rate-table-graph"
          over={(scales) => <DataLine points={points} scales={scales} />}
          series={[
            {
              f: () => Number.NaN,
              color: CHART_COLORS[0],
              label: strings.seriesData,
            },
            {
              f: (x) => ya + rate * (x - a),
              color: CHART_COLORS[3],
              dashed: true,
              label: t(strings.seriesSecant, { rate: digits(fmt, rate) }),
            },
          ]}
          title={t(strings.graphTitleTable, {
            a: digits(fmt, a),
            b: digits(fmt, b),
          })}
          window={window}
        />
      </div>
    </>
  );
}

export function RateResults({
  view,
  units,
  strings,
  fmt,
}: {
  view: Exclude<RateView, { kind: "problem" }>;
  units: Units;
  strings: Strings;
  fmt: Fmt;
}) {
  return view.kind === "function" ? (
    <FunctionRate fmt={fmt} strings={strings} units={units} view={view} />
  ) : (
    <TableRate fmt={fmt} strings={strings} units={units} view={view} />
  );
}
